import React, { useState, useEffect, useRef, useMemo } from "react";
import { Typography, TextField, Button, Container, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ButtonLink from "../../utils/ButtonLink";
import { Link, useLocation, useNavigate } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";

import { getAllDrugs, searchDrugs } from "../../services/drugDataService";

export default function Hero() {
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [searchError, setSearchError] = useState("");
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestEmail, setRequestEmail] = useState("");
  const [requestMedName, setRequestMedName] = useState("");
  const [requestStatus, setRequestStatus] = useState({ loading: false, success: false, error: "" });
  // All loaded drug data (from backend or JSON fallback)
  const [allDrugs, setAllDrugs] = useState([]);
  const [drugsLoading, setDrugsLoading] = useState(true);
  const navigate = useNavigate();
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);

  // Load all drugs once on mount
  useEffect(() => {
    getAllDrugs().then(data => { setAllDrugs(data); setDrugsLoading(false); }).catch(() => { setAllDrugs([]); setDrugsLoading(false); });
  }, []);

  // Helper to get drug data by name (synchronous, uses loaded cache)
  const getDrugByName = (name) => {
    if (!name || allDrugs.length === 0) return undefined;
    const lowerName = name.toLowerCase();
    return allDrugs.find(drug =>
      drug?.drug_metadata?.brand_name?.toLowerCase() === lowerName ||
      drug?.drug_metadata?.generic_name?.toLowerCase() === lowerName
    );
  };

  const popularMedicationsList = [
    { name: "Humira", category: "Arthritis Medication", icon: "💊" },
    { name: "Xarelto", category: "Blood Thinner", icon: "💊" },
    { name: "Januvia", category: "Diabetes Medication", icon: "💊" },
    { name: "Advair", category: "Asthma Medication", icon: "💊" }
  ];

  const popularMedications = useMemo(() => {
    return popularMedicationsList.map(med => {
      const drug = getDrugByName(med.name);
      let usPrice = 0;
      let globalPrice = 0;

      if (drug) {
        let firstDosage = null;
        for (const r of drug.regions || []) {
          for (const p of r.pharmacies || []) {
            if (p.dosages && p.dosages.length > 0) {
              firstDosage = p.dosages[0].dosage_strength;
              break;
            }
          }
          if (firstDosage) break;
        }

        const usPrices = [];
        const euPrices = [];
        const asiaPrices = [];

        for (const r of drug.regions || []) {
          const rName = r.region_name?.toLowerCase() || "";
          let targetArray = null;

          if (rName.includes("usa") || rName.includes("north america")) targetArray = usPrices;
          else if (rName.includes("europe")) targetArray = euPrices;
          else targetArray = asiaPrices;

          for (const p of r.pharmacies || []) {
            for (const dos of p.dosages || []) {
              const price = dos.price_usd ?? dos.price_with_coupon_usd ?? dos.price_cash_usd;
              if (dos.dosage_strength === firstDosage && price !== undefined) {
                targetArray.push(parseFloat(price));
              }
            }
          }
        }

        if (usPrices.length > 0) {
          usPrice = usPrices.reduce((a, b) => a + b, 0) / usPrices.length;
        }

        const euAvg = euPrices.length > 0 ? euPrices.reduce((a, b) => a + b, 0) / euPrices.length : 0;
        const asiaAvg = asiaPrices.length > 0 ? asiaPrices.reduce((a, b) => a + b, 0) / asiaPrices.length : 0;

        const validGlobal = [euAvg, asiaAvg].filter(p => p > 0);
        if (validGlobal.length > 0) {
          globalPrice = Math.min(...validGlobal);
        }
      }

      return {
        ...med,
        usPrice: usPrice > 0
          ? `$${usPrice.toLocaleString("en-US", { maximumFractionDigits: 0 })} US`
          : "N/A",
        globalPrice: globalPrice > 0
          ? `$${globalPrice.toLocaleString("en-US", { maximumFractionDigits: 0 })} Global`
          : "N/A"
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDrugs]);

  const [showPopular, setShowPopular] = useState(false);
  const [activePopularIndex, setActivePopularIndex] = useState(0);
  const [popAnimating, setPopAnimating] = useState(false);

  useEffect(() => {
    if (searchQuery.length >= 3) {
      const lowerQuery = searchQuery.toLowerCase();
      // Filter drugs by brand_name or generic_name from loaded data
      const filteredDrugs = allDrugs.filter(drug =>
        drug?.drug_metadata?.brand_name?.toLowerCase()?.includes(lowerQuery) ||
        drug?.drug_metadata?.generic_name?.toLowerCase()?.includes(lowerQuery)
      );
      const filteredSuggestions = filteredDrugs
        .map(drug => drug?.drug_metadata?.brand_name)
        .filter(Boolean);
      setSuggestions(filteredSuggestions);
      setShowSuggestions(true);
      setShowPopular(false);
      setSearchError("");
      setActiveSuggestionIndex(-1);
    } else if (searchQuery.length === 0 && showPopular) {
      setSuggestions([]);
      setShowSuggestions(false);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery, showPopular, allDrugs]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
        setShowPopular(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (query = searchQuery) => {
    if (query.trim().length < 3) {
      setSearchError("Please enter at least 3 characters.");
      setShowSuggestions(false);
      setShowPopular(false);
      return;
    }

    if (query.trim()) {
      navigate(`/price-compare?medicine=${encodeURIComponent(query)}`);
      setShowSuggestions(false);
      setShowPopular(false);
      setSearchError(""); // 🧹 Clear error on success
    }
  };

  const handleOpenRequestModal = () => {
    setRequestMedName(searchQuery);
    setRequestModalOpen(true);
    setRequestStatus({ loading: false, success: false, error: "" });
  };

  const handleCloseRequestModal = () => {
    setRequestModalOpen(false);
  };

  const handleSubmitRequest = async () => {
    if (!requestMedName.trim()) return;
    setRequestStatus({ loading: true, success: false, error: "" });
    const apiBase = process.env.REACT_APP_API_URL || "https://www.rxpose.org/api";
    try {
      const response = await fetch(`${apiBase}/drug-requests`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          drug_name: requestMedName,
          email: requestEmail
        })
      });
      const data = await response.json();
      if (data.success) {
        setRequestStatus({ loading: false, success: true, error: "" });
        setTimeout(() => {
          handleCloseRequestModal();
          setShowSuggestions(false);
          setSearchQuery("");
        }, 2000);
      } else {
        setRequestStatus({ loading: false, success: false, error: data.error || "Failed to submit request" });
      }
    } catch (err) {
      setRequestStatus({ loading: false, success: false, error: "Network error. Please try again later." });
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    setShowPopular(false);
    handleSearch(suggestion);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions && !showPopular) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (showSuggestions) {
          setActiveSuggestionIndex(prev =>
            prev < suggestions.length - 1 ? prev + 1 : prev
          );
        }
        break;
      case "ArrowUp":
        e.preventDefault();
        if (showSuggestions) {
          setActiveSuggestionIndex(prev => (prev > 0 ? prev - 1 : -1));
        }
        break;
      case "Enter":
        e.preventDefault();
        if (activeSuggestionIndex >= 0 && showSuggestions) {
          handleSuggestionClick(suggestions[activeSuggestionIndex]);
        } else {
          handleSearch();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setShowPopular(false);
        setActiveSuggestionIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!showPopular || drugsLoading) return;
    const interval = setInterval(() => {
      setPopAnimating(true);
      setTimeout(() => {
        setActivePopularIndex(prev => (prev + 1) % popularMedications.length);
        setPopAnimating(false);
      }, 300);
    }, 2500);
    return () => clearInterval(interval);
  }, [showPopular, drugsLoading, popularMedications.length]);

  const location = useLocation();
  const isActive = (path) => (location.pathname === path ? "active" : "");

  return (
    <div className="hero_section_main" style={{ background: "#E0FAF8" }}>
      <Container>
        <div className="hero_content" style={{ position: "relative", zIndex: 100 }}>
          <Typography variant="h4" className="heading_two" gutterBottom>
            Exposing Prescription Drug Price Disparities <br />
            <Typography variant="span" className="green_text">
              Why do Americans pay more<br />
              for prescription drugs?
            </Typography>
          </Typography>

          <div className="form_search" style={{ position: "relative", zIndex: 100 }}>
            <TextField
              ref={inputRef}
              variant="outlined"
              placeholder="Enter a Medicine Name"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setSearchError(""); // 🧹 Clear error on change
                if (e.target.value.length === 0) {
                  setShowPopular(true);
                }
              }}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                if (searchQuery.length === 0) {
                  setShowPopular(true);
                } else if (searchQuery.length >= 3) {
                  setShowSuggestions(true);
                }
              }}
              sx={{ marginRight: "1rem", flexGrow: 1 }}
              autoComplete="off"
              error={!!searchError}
              helperText={searchError}
            />

            {/* Popular Medications Dropdown — single row cycling */}
            {showPopular && searchQuery.length === 0 && (
              <div
                ref={suggestionsRef}
                style={{
                  width: "100%",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  borderRadius: "12px",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                  zIndex: 9999,
                  overflow: "hidden",
                  marginTop: "8px",
                  border: "1px solid #eef2f7",
                }}
              >
                <style>{`
                  @keyframes spin { to { transform: rotate(360deg); } }
                  @keyframes slideInUp {
                    0%   { opacity: 0; transform: translateY(12px); }
                    100% { opacity: 1; transform: translateY(0); }
                  }
                  @keyframes slideOutUp {
                    0%   { opacity: 1; transform: translateY(0); }
                    100% { opacity: 0; transform: translateY(-12px); }
                  }
                `}</style>

                {/* Header label */}
                <div style={{
                  padding: "10px 18px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderBottom: "1px solid #f0f4f8",
                  background: "#fafcff",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "7px" }}>
                    <span style={{ fontSize: "15px" }}>🔥</span>
                    <span style={{ fontWeight: "700", fontSize: "13px", color: "#062a3f", letterSpacing: "0.05em", textTransform: "uppercase" }}>
                      Popular Medications
                    </span>
                  </div>
                  {/* Dot indicators */}
                  {!drugsLoading && (
                    <div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
                      {popularMedications.map((_, i) => (
                        <div
                          key={i}
                          onClick={() => { setPopAnimating(false); setActivePopularIndex(i); }}
                          style={{
                            width: i === activePopularIndex ? "18px" : "6px",
                            height: "6px",
                            borderRadius: "3px",
                            background: i === activePopularIndex ? "#43C5B9" : "#cbd5e0",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                          }}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Single animated row */}
                <div style={{ height: "68px", position: "relative", overflow: "hidden" }}>
                  {drugsLoading ? (
                    <div style={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                      <div style={{
                        width: "22px", height: "22px",
                        border: "2px solid #e2e8f0",
                        borderTop: "2px solid #43C5B9",
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }} />
                      <span style={{ fontSize: "13px", color: "#a0aec0" }}>Loading…</span>
                    </div>
                  ) : (() => {
                    const med = popularMedications[activePopularIndex];
                    const accentColors = ["#43C5B9", "#7C6FCD", "#F6AD55", "#38A169"];
                    const accent = accentColors[activePopularIndex % accentColors.length];
                    return (
                      <div
                        key={activePopularIndex}
                        onClick={() => handleSuggestionClick(med.name)}
                        style={{
                          position: "absolute",
                          inset: 0,
                          padding: "0 18px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          cursor: "pointer",
                          background: "white",
                          animation: popAnimating
                            ? "slideOutUp 0.25s ease forwards"
                            : "slideInUp 0.35s cubic-bezier(0.22,1,0.36,1) forwards",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f7fafc"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                      >
                        {/* Icon + name + category */}
                        <div style={{ display: "flex", alignItems: "center", gap: "13px" }}>
                          <div style={{
                            width: "40px", height: "40px",
                            borderRadius: "10px",
                            background: `linear-gradient(135deg, ${accent}18, ${accent}35)`,
                            border: `1.5px solid ${accent}50`,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "19px", flexShrink: 0,
                          }}>
                            {med.icon}
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                            <span style={{ fontWeight: "700", fontSize: "15px", color: "#1a202c" }}>{med.name}</span>
                            <span style={{ fontSize: "12px", color: accent, fontWeight: "600", marginTop: "1px" }}>{med.category}</span>
                          </div>
                        </div>

                        {/* Prices */}
                        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                          <span style={{
                            fontWeight: "700", fontSize: "13px", color: "#C53030",
                            background: "#FFF5F5", border: "1px solid #FED7D7",
                            padding: "3px 10px", borderRadius: "20px",
                          }}>{med.usPrice}</span>
                          <span style={{
                            fontWeight: "700", fontSize: "13px", color: "#2B6CB0",
                            background: "#EBF8FF", border: "1px solid #BEE3F8",
                            padding: "3px 10px", borderRadius: "20px",
                          }}>{med.globalPrice}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* Autocomplete Suggestions */}
            {showSuggestions && (
              <div
                ref={suggestionsRef}
                style={{
                  width: "100%",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  backgroundColor: "white",
                  border: "1px solid #ccc",
                  borderTop: "none",
                  borderRadius: "0 0 4px 4px",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  zIndex: 1000,
                  maxHeight: "300px",
                  overflowY: "auto",
                  marginTop: "8px"
                }}
              >
                {suggestions.length > 0 ? (
                  <div style={{
                    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    borderRadius: "12px",
                    padding: "8px",
                    boxShadow: "0 8px 32px rgba(102, 126, 234, 0.3)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)"
                  }}>
                    {suggestions.map((suggestion, index) => (
                      <div
                        key={suggestion}
                        onClick={() => handleSuggestionClick(suggestion)}
                        style={{
                          margin: "4px 0",
                          padding: "16px 20px",
                          cursor: "pointer",
                          background: index === activeSuggestionIndex
                            ? "linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0.85))"
                            : "linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))",
                          borderRadius: "8px",
                          display: "flex",
                          flexDirection: "column",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                          transform: index === activeSuggestionIndex ? "translateY(-2px) scale(1.02)" : "translateY(0) scale(1)",
                          boxShadow: index === activeSuggestionIndex
                            ? "0 8px 25px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(102, 126, 234, 0.4)"
                            : "0 2px 8px rgba(0, 0, 0, 0.1)",
                          backdropFilter: "blur(10px)",
                          border: index === activeSuggestionIndex
                            ? "2px solid rgba(102, 126, 234, 0.6)"
                            : "1px solid rgba(255, 255, 255, 0.3)",
                          position: "relative",
                          overflow: "hidden"
                        }}
                        onMouseEnter={() => setActiveSuggestionIndex(index)}
                        onMouseLeave={() => setActiveSuggestionIndex(-1)}
                      >
                        {/* Animated background gradient on hover */}
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "linear-gradient(45deg, rgba(102, 126, 234, 0.1), rgba(118, 75, 162, 0.1))",
                          opacity: index === activeSuggestionIndex ? 1 : 0,
                          transition: "opacity 0.3s ease",
                          borderRadius: "6px"
                        }} />

                        {/* Medicine icon */}
                        <div style={{
                          position: "absolute",
                          right: "16px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          fontSize: "20px",
                          opacity: 0.6,
                          color: "#667eea"
                        }}>
                          💊
                        </div>

                        <div style={{ position: "relative", zIndex: 1, paddingRight: "40px" }}>
                          <div style={{
                            display: "flex",
                            alignItems: "center",
                            marginBottom: "6px"
                          }}>
                            <div style={{
                              width: "4px",
                              height: "16px",
                              background: "linear-gradient(135deg, #667eea, #764ba2)",
                              borderRadius: "2px",
                              marginRight: "12px",
                              opacity: index === activeSuggestionIndex ? 1 : 0.6,
                              transition: "opacity 0.3s ease"
                            }} />
                            <span style={{
                              fontWeight: "600",
                              fontSize: "15px",
                              color: "#2d3748",
                              letterSpacing: "0.025em"
                            }}>
                              {suggestion}
                            </span>
                          </div>

                          <span style={{
                            fontSize: "13px",
                            color: "#4a5568",
                            lineHeight: "1.4",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            marginLeft: "16px",
                            opacity: 0.8
                          }}>
                            {(() => {
                              const drug = getDrugByName(suggestion);
                              if (drug) {
                                return `${drug.drug_metadata.generic_name} - ${drug.drug_metadata.drug_class}`;
                              }
                              return "Medication information available";
                            })()}
                          </span>
                        </div>

                        {/* Shimmer effect */}
                        <div style={{
                          position: "absolute",
                          top: 0,
                          left: "-100%",
                          width: "100%",
                          height: "100%",
                          background: "linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent)",
                          animation: index === activeSuggestionIndex ? "shimmer 2s infinite" : "none"
                        }} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: "20px 24px",
                    background: "linear-gradient(135deg, #f7fafc, #edf2f7)",
                    borderRadius: "12px",
                    border: "2px dashed #cbd5e0",
                    color: "#4a5568",
                  }}>
                    <div style={{ fontSize: "14px", fontWeight: "600", color: "#2d3748", marginBottom: "6px" }}>
                      Can't find <strong style={{ color: "#062a3f" }}>{searchQuery}</strong>? Request it below and we'll add it. We currently cover brand-name prescription drugs only — no generics.
                    </div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "12px" }}>
                      <Button variant="contained" onClick={handleOpenRequestModal} size="small" sx={{ borderRadius: "20px", fontWeight: 700, bgcolor: "#43C5B9", color: "#fff", "&:hover": { bgcolor: "#38b2a8" }, mx: "auto" }}>
                        Request Drug
                      </Button>
                      <Tooltip
                        title={
                          <div style={{ maxWidth: "300px", padding: "6px 2px", fontSize: "13px", lineHeight: "1.65" }}>
                            RXpose tracks brand-name prescription drugs still under patent — where the price gap between the US and the rest of the world is widest. Generic drugs are excluded by design; once competition enters, the market corrects itself. The scandal is what happens before that.
                          </div>
                        }
                        arrow
                        placement="bottom-start"
                        enterTouchDelay={0}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              bgcolor: "#062a3f",
                              "& .MuiTooltip-arrow": { color: "#062a3f" },
                              borderRadius: "10px",
                              p: 1.5,
                              maxWidth: 320,
                              boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                            }
                          }
                        }}
                      >
                        <span style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          color: "#43C5B9",
                          fontSize: "11px",
                          fontWeight: 600,
                          cursor: "pointer",
                          opacity: 0.85,
                          userSelect: "none",
                        }}>
                          <InfoOutlinedIcon sx={{ fontSize: "14px" }} />
                          Why no generics?
                        </span>
                      </Tooltip>
                    </div>
                  </div>
                )}

                {/* Add this CSS for the shimmer animation */}
                <style jsx>{`
  @keyframes shimmer {
    0% {
      left: -100%;
    }
    100% {
      left: 100%;
    }
  }
`}</style>
              </div>
            )}

            <Button variant="contained" color="primary" onClick={() => handleSearch()}>
              Compare Prices
            </Button>
          </div>

          <div style={{ display: "inline-flex", alignItems: "center", gap: "4px", marginTop: "36px", justifyContent: "center" }} className="hero_btn_row">
            <ButtonLink
              url="/medicines"
              title="Browse Popular Medications"
            />
            <Tooltip
              title={
                <div style={{ maxWidth: "300px", padding: "6px 2px", fontSize: "13px", lineHeight: "1.65" }}>
                  Once a drug goes generic, competition drives prices sharply lower in the U.S. Sites like GoodRx and Mark Cuban Cost Plus already help consumers find those affordable options. RXpose.org instead exposes the real gap: name-brand medications still under patent, where lack of free-market competition keeps U.S. prices dramatically higher than overseas.
                </div>
              }
              arrow
              placement="bottom-start"
              enterTouchDelay={0}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "#062a3f",
                    "& .MuiTooltip-arrow": { color: "#062a3f" },
                    borderRadius: "10px",
                    p: 1.5,
                    maxWidth: 320,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                  }
                }
              }}
            >
              <Button
                size="small"
                startIcon={<InfoOutlinedIcon fontSize="small" />}
                sx={{
                  color: "#43C5B9",
                  bgcolor: "rgba(67,197,185,0.1)",
                  border: "1.5px solid rgba(67,197,185,0.3)",
                  borderRadius: "20px",
                  fontSize: "12px",
                  fontWeight: 700,
                  px: 1.5,
                  py: 0.7,
                  whiteSpace: { xs: "normal", sm: "nowrap" },
                  textTransform: "none",
                  "&:hover": { bgcolor: "rgba(67,197,185,0.2)" },
                }}
              >
                Why we don't do generic
              </Button>
            </Tooltip>
          </div>
        </div>
      </Container>

      {/* Drug Request Modal */}
      <Dialog
        open={requestModalOpen}
        onClose={handleCloseRequestModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            p: 1,
            boxShadow: "0 20px 60px rgba(6,42,63,0.15)",
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 2.5, px: 3, fontWeight: 800, fontSize: "22px", color: "#062a3f", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          Request Medication
          <Tooltip
            title={
              <div style={{ maxWidth: "300px", padding: "6px 2px", fontSize: "13px", lineHeight: "1.65" }}>
                RXpose tracks brand-name prescription drugs still under patent — where the price gap between the US and the rest of the world is widest. Generic drugs are excluded by design; once competition enters, the market corrects itself. The scandal is what happens before that.
              </div>
            }
            arrow
            placement="bottom-end"
            enterTouchDelay={0}
            componentsProps={{
              tooltip: {
                sx: {
                  bgcolor: "#062a3f",
                  "& .MuiTooltip-arrow": { color: "#062a3f" },
                  borderRadius: "10px",
                  p: 1.5,
                  maxWidth: 320,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
                }
              }
            }}
          >
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              color: "#43C5B9",
              fontSize: "11px",
              fontWeight: 600,
              cursor: "pointer",
              opacity: 0.85,
              userSelect: "none",
            }}>
              <InfoOutlinedIcon sx={{ fontSize: "15px" }} />
              Why no generics?
            </span>
          </Tooltip>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <DialogContentText sx={{ fontSize: "15px", color: "#4a5568", lineHeight: 1.7, mb: 2 }}>
            Can't find <strong style={{ color: "#062a3f" }}>{requestMedName || searchQuery}</strong>? Request it below and we'll add it. We currently cover brand-name prescription drugs only — no generics.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Medication Name"
            type="text"
            fullWidth
            variant="outlined"
            value={requestMedName}
            onChange={(e) => setRequestMedName(e.target.value)}
            sx={{
              mt: 0,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "&.Mui-focused fieldset": { borderColor: "#43C5B9" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#43C5B9" },
              "& .MuiInputLabel-root": { fontWeight: 600 },
            }}
          />
          <TextField
            margin="dense"
            label="Email Address (Optional)"
            type="email"
            fullWidth
            variant="outlined"
            value={requestEmail}
            onChange={(e) => setRequestEmail(e.target.value)}
            sx={{
              mt: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                "&.Mui-focused fieldset": { borderColor: "#43C5B9" },
              },
              "& .MuiInputLabel-root.Mui-focused": { color: "#43C5B9" },
            }}
          />
          {requestStatus.error && (
            <Typography color="error" variant="body2" sx={{ mt: 1.5, fontSize: "13px" }}>
              {requestStatus.error}
            </Typography>
          )}
          {requestStatus.success && (
            <Typography color="success.main" variant="body2" sx={{ mt: 1.5, fontSize: "13px" }}>
              Request submitted successfully!
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleCloseRequestModal}
            disabled={requestStatus.loading}
            sx={{
              color: "#43C5B9",
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "0.08em",
              "&:hover": { bgcolor: "rgba(67,197,185,0.07)" },
            }}
          >
            CANCEL
          </Button>
          <Button
            onClick={handleSubmitRequest}
            variant="contained"
            disabled={requestStatus.loading || requestStatus.success}
            sx={{
              bgcolor: "#2563a8",
              fontWeight: 700,
              fontSize: "13px",
              letterSpacing: "0.08em",
              borderRadius: "8px",
              px: 3,
              py: 1.2,
              boxShadow: "0 4px 14px rgba(37,99,168,0.3)",
              "&:hover": { bgcolor: "#1d4f8c", boxShadow: "0 6px 18px rgba(37,99,168,0.4)" },
              "&.Mui-disabled": { bgcolor: "#94a3b8", color: "#fff" },
            }}
          >
            {requestStatus.loading ? "SUBMITTING..." : "SUBMIT REQUEST"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
